/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { IMediaCategoryListModel, IMediaListModel } from "@nf/common";

import dynamic from "next/dynamic";
const ActionsHandler = dynamic<IActionsHandlerProps>(
  () => import("components/ActionsHandler").then((mod) => mod.ActionsHandler),
  { ssr: false }
);
const GenreGrid = dynamic<TGenresGrid>(() =>
  import("screens/GenersScreen/GenreGrid").then((mod) => mod.GenreGrid)
);
const MediaCategoryDrawer = dynamic<MediaCategoryDrawerProps>(
  () =>
    import("components/Drawers/MediaCategoryDrawer").then(
      (mod) => mod.MediaCategoryDrawer
    ),
  { ssr: false }
);
const MediaSortDrawer = dynamic<IMediaSortDrawer>(
  () =>
    import("components/Drawers/MediaSortDrawer").then(
      (mod) => mod.MediaSortDrawer
    ),
  { ssr: false }
);
const GenreSEO = dynamic<GenreSEOProp>(() =>
  import("components/SEO").then((mod) => mod.GenreSEO)
);
import styles from "./GenresScreen.module.scss";
import { isServerSideRender } from "@nf/helpers";
import { TGenresGrid } from "./GenreGrid";
import { InfiniteScrollWrapper } from "./InfiniteScrollWrapper";
import {
  useBrandingChangeEvent,
  useGenreRouter,
  useGetSortedList,
  useInfiniteScrollData,
  useUpdateMediaListStore,
  useGetCategoryById,
} from "hooks";
import { GenreSEOProp } from "components/SEO";
import { MediaCategoryDrawerProps } from "components/Drawers/MediaCategoryDrawer";
import { IActionsHandlerProps } from "components";
import { IMediaSortDrawer } from "components/Drawers/MediaSortDrawer";

export const GenresScreen = ({
  categories,
  mediaList: mediaListFromProps,
}: {
  categories: IMediaCategoryListModel;
  mediaList: IMediaListModel;
}) => {
  const { mediaListId, params } = useGenreRouter();

  useUpdateMediaListStore(mediaListId, mediaListFromProps);

  const queryParams = {
    ...params,
    category: mediaListId,
    type: "genre",
  };

  const { mediaList, getList, getMore } = useInfiniteScrollData(
    queryParams,
    mediaListId
  );
  useGetSortedList(getList);
  useBrandingChangeEvent(mediaListId);
  const activeCategory = useGetCategoryById(categories, mediaListId);

  const mediaListData = isServerSideRender ? mediaListFromProps : mediaList;

  return (
    <>
      <GenreSEO activeCategoryName={activeCategory.CategoryName} />
      <InfiniteScrollWrapper mediaList={mediaListData} getMore={getMore}>
        <>
          <div className={styles.buttonContainer}>
            {!isServerSideRender && (
              <>
                <MediaCategoryDrawer
                  categories={categories}
                  activeCategory={activeCategory}
                  type="genre"
                />
                <MediaSortDrawer />
              </>
            )}
          </div>
          {!isServerSideRender && (
            <ActionsHandler centeredError error={mediaList.Error} />
          )}
          <div className={styles.content}>
            <GenreGrid mediaList={mediaListData} />
          </div>
        </>
      </InfiniteScrollWrapper>
    </>
  );
};

export default GenresScreen;
